import { FC, useRef, useState } from 'react'
import { IconButton, Typography, ClickAwayListener } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { ActiveFilter, DateFilterValue } from '../Devices'
import {
  StyledFormControl,
  PopperInner,
  MenuItem,
  MenuItemContent,
  MenuItemLabel,
  MenuItemValueWrapper,
  Popper
} from './OtherFilters.styles'
import { FilterButton, popperPropsSx } from './filterChip.styles'
import { ChipTooltip } from 'ui/components/tooltip/ChipTooltip'

const popperModifiers = [
  {
    options: {
      altAxis: true,
      altBoundary: true,
      tether: true
    }
  }
]

interface Props {
  hiddenFilters: ActiveFilter[]
  handleResetFilter: (filterName: string, filterValue: string[]) => void
}

function isDateFilterValue(value: string[] | DateFilterValue): value is DateFilterValue {
  return typeof value === 'object' && value !== null && 'dateFrom' in value && 'dateTo' in value
}

function formatDateFilterDisplay(dateFilter: DateFilterValue): string {
  const from = dateFilter.dateFrom || 'N/A'
  const to = dateFilter.dateTo || 'N/A'
  return `${from} - ${to}`
}

const OtherFilters: FC<Props> = ({ hiddenFilters, handleResetFilter }) => {
  const ref = useRef(null)
  const [open, setOpen] = useState(false)

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  if (!hiddenFilters.length) return null
  return (
    <StyledFormControl onClick={(e) => e.preventDefault()}>
      <FilterButton onClick={handleOpen} ref={ref} disableRipple disableTouchRipple disableFocusRipple>
        <Typography className="filter-chip-filter-name">Other filters:</Typography>
        <Typography className="text">+{hiddenFilters.length}</Typography>
        <KeyboardArrowDownIcon
          sx={{ transform: `rotate(${open ? '180deg' : '0'})`, transition: 'all .1s ease-in-out' }}
        />
      </FilterButton>

      <Popper open={open} anchorEl={(ref as any)?.current || null} placement="bottom-start" modifiers={popperModifiers}>
        <ClickAwayListener onClickAway={handleClose}>
          <PopperInner>
            {hiddenFilters.map((filter, index) => {
              const filterValue = filter.filterValue
              const isDateFilter = isDateFilterValue(filterValue)

              return (
                <MenuItem
                  key={index}
                  value={filter.filterName}
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                  }}
                  disableRipple
                  disableTouchRipple
                >
                  <MenuItemContent className="menu-item-content">
                    <MenuItemLabel>{filter.filterName}</MenuItemLabel>
                    <MenuItemValueWrapper>
                      {isDateFilter ? (
                        <>
                          <Typography>{formatDateFilterDisplay(filterValue)}</Typography>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation()
                              handleResetFilter(filter.filterName, [])
                            }}
                            size="small"
                          >
                            <CloseIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          {filterValue.length > 1 ? (
                            <ChipTooltip
                              placement="bottom-start"
                              PopperProps={popperPropsSx}
                              title={
                                <Typography className="tooltip-text" style={{ whiteSpace: 'pre-line' }}>
                                  {filterValue.join('\n')}
                                </Typography>
                              }
                            >
                              <span className="value-with-count">
                                <Typography className="truncated-value">{filterValue[0]}</Typography>
                                <Typography className="additional">(+{filterValue.length - 1})</Typography>
                              </span>
                            </ChipTooltip>
                          ) : (
                            <Typography>{filterValue[0]}</Typography>
                          )}
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation()
                              handleResetFilter(filter.filterName, filterValue)
                            }}
                            size="small"
                          >
                            <CloseIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </>
                      )}
                    </MenuItemValueWrapper>
                  </MenuItemContent>
                </MenuItem>
              )
            })}
          </PopperInner>
        </ClickAwayListener>
      </Popper>
    </StyledFormControl>
  )
}

export default OtherFilters
